/**
 * API методы для работы с семьями пациентов
 * Дата: 09.01.2026 - Создан для TODO-029 (Patient Families)
 * Связан с: internal/handler/family.go, frontend/src/hooks/useFamilies.ts
 */
import { apiClient } from './client';
import { PatientFamily, DuplicatePhoneGroup, CreateFamilyRequest, UpdateFamilyRequest, CreateFamilyFromDuplicatesRequest } from './types';

export const familiesApi = {
  // Список семей с пагинацией
  list: async (tenantId?: string, offset?: number, limit?: number): Promise<{ families: PatientFamily[]; total: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    if (offset !== undefined) params.offset = String(offset);
    if (limit !== undefined) params.limit = String(limit);
    const response = await apiClient.get('/families', { params });
    return response.data;
  },

  // Получить семью по ID
  get: async (id: string, tenantId?: string): Promise<PatientFamily> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get(`/families/${id}`, { params });
    return response.data;
  },

  // Создать семью
  create: async (data: CreateFamilyRequest, tenantId?: string): Promise<PatientFamily> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post('/families', data, { params });
    return response.data;
  },

  // Обновить семью
  update: async (id: string, data: UpdateFamilyRequest, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    await apiClient.put(`/families/${id}`, data, { params });
  },

  // Удалить семью
  delete: async (id: string, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    await apiClient.delete(`/families/${id}`, { params });
  },

  // Добавить члена в семью
  addMember: async (familyId: string, patientId: string, role?: string, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    await apiClient.post(`/families/${familyId}/members`, { patient_id: patientId, role }, { params });
  },

  // Удалить члена из семьи
  removeMember: async (familyId: string, patientId: string, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    await apiClient.delete(`/families/${familyId}/members/${patientId}`, { params });
  },

  // Установить primary contact
  setPrimary: async (familyId: string, patientId: string, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    await apiClient.put(`/families/${familyId}/primary/${patientId}`, {}, { params });
  },

  // Найти дубликаты телефонов
  findDuplicates: async (tenantId?: string): Promise<{ duplicates: DuplicatePhoneGroup[] }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get('/families/duplicates', { params });
    return response.data;
  },

  // Создать семью из дубликатов
  createFromDuplicates: async (data: CreateFamilyFromDuplicatesRequest, tenantId?: string): Promise<PatientFamily> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post('/families/from-duplicates', data, { params });
    return response.data;
  },
};

